import Checkbox from "@material-ui/core/Checkbox";
import MenuItem from "@material-ui/core/MenuItem";
import grey from "@material-ui/core/colors/grey";
import orange from "@material-ui/core/colors/orange";
import red from "@material-ui/core/colors/red";
import Paper from "@material-ui/core/Paper";
import { createStyles, Theme, withStyles, WithStyles, withTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ZoomIn from "@material-ui/icons/ZoomIn";
import ZoomOut from "@material-ui/icons/ZoomOut";
import classnames from "classnames";
import React, { useContext, useEffect } from "react";
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, Text, XAxis, YAxis, Legend, Bar as StackedChartBar } from "recharts";
import { Loading } from "../components/Loading";
import { Query, Report, Series, GroupSeries } from "../lib/api/charts";
import { ChartMenu } from "./ChartMenu";
import { OemContext } from "../contexts/OemContext";
import numeral from "numeral";
import { BenchmarkContext } from "../contexts/BenchmarkContext";

import { GroupBarChartContext } from "../contexts/GroupBarChartContext";
import * as d3Scale from "d3-scale-chromatic";
import { ReportSelector } from "./GroupReportSelector";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import History from "@material-ui/icons/History";
import { useMediaQuery } from "react-responsive";
import { ChartDialog } from "../lib/shared/ChartDialog";
import { DealerContext } from "../contexts/DealerContext";
import { Grid } from "@material-ui/core";
import { ClickAwayListener } from "@material-ui/core";
import { BenchmarkSelection } from "../Benchmark";
import { getBenchmarkColor } from "./GroupSeriesChart";
import { formatYTick } from "./GroupSeriesChart";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      flex: "1",
      fontFamily:
        "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', Helvetica, Arial, sans-serif",
    },
    chartPaperRoot: {
      display: "flex",
      flexDirection: "column",
      padding: `${theme.spacing.unit * 2}px`,
      flex: "1",
      backgroundColor: "#FFF",
      // boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1),0 1px 2px 0 rgba(0, 0, 0, 0.06) !important",
      borderRadius: "5px",
      border: "1px #DADADA solid",
      ["@media (max-width:450px)"]: {
        padding: "0px",
      },
    },
    headingContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    chart: {
      flex: "1",
    },
    menu: {
      // margin: "-0.85rem"
      display: "flex",
    },
    generalNews: {
      background: "#800080",
    },
    dataQualityNews: {
      background: "#ADD8E6",
    },
    dealerPerformanceNews: {
      background: "#003366",
    },
    mediumInput: {
      minWidth: "200px",
      maxWidth: "300px",
    },
    benchmarkSelection: {
      gridColumn: "6/8",
      "@media(max-width: 1230px)": {
        gridColumn: "4/6",
      },
    },
    searchInput: {
      "@media(max-width: 900px)": {
        marginBottom: "20px",
      },
      [theme.breakpoints.up("md")]: {
        width: "auto",
      },
      borderRadius: theme.shape.borderRadius,
      flex: "0 0 auto",
      fontSize: "12px",
      minWidth: "140px",
      height: "auto",
      color: "#333",
      fontWeight: "bold",
      // marginTop: theme.spacing.unit,
      fontFamily:
        "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', Helvetica, Arial, sans-serif",
      marginRight: "0px",
    },
  });

const stylesAlt = (theme: Theme) =>
  createStyles({
    root: {
      flex: "1",
      fontFamily:
        "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', Helvetica, Arial, sans-serif",
    },
    chartPaperRoot: {
      display: "flex",
      flexDirection: "column",
      padding: `${theme.spacing.unit * 2}px`,
      flex: "1",
      backgroundColor: "#FFF",
      border: "solid 1px #e0e0e0",
      borderRadius: "3px",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1),0 1px 2px 0 rgba(0, 0, 0, 0.06) !important",
      ["@media (max-width:450px)"]: {
        padding: "0px",
      },
    },
    headingContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    chart: {
      flex: "1",
    },
    menu: {
      // margin: "-0.85rem"
      display: "flex",
    },
    generalNews: {
      background: "#800080",
    },
    dataQualityNews: {
      background: "#ADD8E6",
    },
    dealerPerformanceNews: {
      background: "#003366",
    },
  });

export type ChartProps = {
  barColor?: string;
  lineColor?: string;
  query: Query;
  title?: string;
  report: Report;
  theme: Theme;
  handleSelection?(key: Report): void;
  showBenchmark?: boolean;
  showMedian?: boolean;
  showTarget?: boolean;
  measureAgainst?: string;
  forcedHeight?: string;
  renderMenu?: () => React.ReactElement<any>;
  chartOptions?: any;
  chartType?: "bar" | "line" | "pie" | "period";
  measure?: any;
  type?: string;
  openDialog?: boolean;
  setOpenDialog?: (value: boolean) => void;
  isDesktopOrLaptop?: boolean;
  lockScorecardOptions?: boolean;
} & WithStyles<typeof styles>;

const BarTooltip = ({ active, payload, query, extraBenchmarks }: any) => {
  if (active && payload && payload[0]) {
    return (
      <Paper style={{ padding: "0.5rem" }}>
        {query.meta.chartType == "period" && (
          <Typography variant="caption" key={payload[0].payload.dealer} component="span">
            <b>
              {payload[0].payload.dealer} {payload[0].payload.dates[1]}:
            </b>{" "}
            {payload[0].payload[`${payload[0].payload.dealer} actualFormatted-1`]}
            <br />
            <b>
              {payload[0].payload.dealer} {payload[0].payload.dates[0]}:
            </b>{" "}
            {payload[0].payload[`${payload[0].payload.dealer} actualFormatted`]}
          </Typography>
        )}
        {query.meta.chartType == "bar" && (
          <Typography variant="caption" key={payload[0].payload.dealer} component="span">
            <b>
              {payload[0].payload.dealer} {payload[0].payload.dates[0]}:
            </b>{" "}
            {payload[0].payload[`${payload[0].payload.dealer} actualFormatted`]}
          </Typography>
        )}
        {payload[0].payload.benchmarkFormatted && (
          <Typography variant="caption" component="span">
            <b>Benchmark:</b> {payload[0].payload.benchmarkFormatted}
          </Typography>
        )}
        {payload[0].payload.medianFormatted && (
          <Typography variant="caption" component="span">
            <b>Median:</b> {payload[0].payload.medianFormatted}
          </Typography>
        )}
        {extraBenchmarks
          ? extraBenchmarks.map(b => {
              return (
                <Typography variant="caption" component="span">
                  <b>{b}</b> {payload[0].payload[`extra_${b}Formatted`]}
                </Typography>
              );
            })
          : null}
      </Paper>
    );
  }
  return null;
};

const renderLegend = (props: any) => {
  const { payload } = props;
  // These styles have been obtained from inspecting the default rechart rendering.
  return (
    <ul className="recharts-default-legend" style={{ padding: "0px", margin: "0px", textAlign: "center" }}>
      {(payload || []).map((entry: any, index: any) => {
        if (entry.value == "actual") return null; // We never want to display the legend for Actual, this was requested by the Customer.

        if (entry.dataKey == "benchmark" || entry.dataKey == "median" || entry.dataKey.includes("extra_")) {
          return (
            <li
              key={`recharts-legend-item legend-item-${index}`}
              className={`recharts-legend-item legend-item-${index}`}
              style={{ display: "inline-block", marginRight: "10px" }}
            >
              <svg
                className="recharts-surface"
                width="14"
                height="14"
                viewBox="0 0 32 32"
                version="1.1"
                style={{ display: "inline-block", verticalAlign: "middle", marginRight: "4px" }}
              >
                {entry.type == "line" ? (
                  <path strokeWidth="6" fill="none" stroke={entry.color} d="M 10 16 L 30 16" className="recharts-legend-icon" strokeDasharray="3 3" />
                ) : (
                  <path stroke="none" fill={entry.color} d="M0,4h32v24h-32z" className="recharts-legend-icon" />
                )}
              </svg>
              <span className={"recharts-legend-item-text"}>{entry.value}</span>
            </li>
          );
        }

        return (
          <li
            key={`recharts-legend-item legend-item-${index}`}
            className={`recharts-legend-item legend-item-${index}`}
            style={{ display: "inline-block", marginRight: "10px" }}
          >
            <svg
              className="recharts-surface"
              width="14"
              height="14"
              viewBox="0 0 32 32"
              version="1.1"
              style={{ display: "inline-block", verticalAlign: "middle", marginRight: "4px" }}
            >
              {entry.type == "line" ? (
                <path
                  strokeWidth="4"
                  fill="none"
                  stroke={entry.color}
                  d="M0,16h10.666666666666666
  A5.333333333333333,5.333333333333333,0,1,1,21.333333333333332,16
  H32M21.333333333333332,16
  A5.333333333333333,5.333333333333333,0,1,1,10.666666666666666,16"
                  className="recharts-legend-icon"
                />
              ) : (
                <path stroke="none" fill={entry.color} d="M0,4h32v24h-32z" className="recharts-legend-icon" />
              )}
            </svg>
            <span className={"recharts-legend-item-text"}>{entry.value}</span>
          </li>
        );
      })}
    </ul>
  );
};

export type ChartInnerProps = {
  theme: Theme;
  series: GroupSeries;
  report: Report;
  title: string;
  handleSelection?(key: Report): void;
  showBenchmark?: boolean;
  showMedian?: boolean;
  showTarget?: boolean;
  measureAgainst?: string;
  menu: React.ReactElement<any>;
  forcedHeight: string;
  query: Query;
  selectedDealers: any[];
  chartOptions?: any;
  chartType?: "bar" | "line" | "pie" | "period";
  extraBenchmarks?: string[];
} & WithStyles<typeof styles>;

const CustomizedXAxisTick = ({ x, y, payload }) => {
  return <Text angle={90} fill="#636463" x={x} y={y} width={200} textAnchor="start" verticalAnchor="start">{`${payload.value}`}</Text>;
};

const actualColour = (series: Series, value) => {
  if (series.classification == "income") {
    if (value.actual < value.median) {
      return red[500];
    }

    if (value.actual <= value.benchmark && value.actual >= value.median) {
      return orange[500];
    }

    return grey[500];
  }

  if (value.actual > value.median) {
    return red[500];
  }

  if (value.actual >= value.benchmark && value.actual <= value.median) {
    return orange[500];
  }

  return grey[500];
};

// Setting the width to the responsive container to 99% as per this issue that is found with recharts
// https://github.com/recharts/recharts/issues/172
const ChartInner: React.FunctionComponent<ChartInnerProps> = ({
  theme,
  series,
  classes,
  report,
  handleSelection,
  measureAgainst,
  showBenchmark,
  showMedian,
  showTarget,
  menu,
  forcedHeight,
  title,
  query,
  selectedDealers,
  chartOptions,
  chartType,
  extraBenchmarks,
}) => {
  const { oems } = useContext(OemContext);
  const { selected: benchmarks, benchmarks: available, loading } = useContext(BenchmarkContext);

  const benchmarkLine = "#20639B";
  const medianLine = "#3cAEA3";
  const targetsLine = "#F6D55C";

  if (!loading.loaded) {
    return <Loading />;
  }

  const dealers = ((selectedDealers || []).filter(dealer => dealer.active) || []).map(dealer => dealer.name);
  const rows = series.rows.filter(row => {
    return (selectedDealers.find(e => e.name == (row as any).dealerName) || {}).active;
  });
  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-device-width: 1280px)",
  });

  return (
    <Paper className={classnames(classes.chartPaperRoot)} style={{ height: forcedHeight }}>
      <div className={classnames(classes.headingContainer)}>
        <ReportSelector handleSelection={handleSelection} report={report} title={series.name} chartOptions={chartOptions} />
        <div className={classnames(classes.menu)}>{menu || null}</div>
      </div>
      <div className={classes.chart}>
        <ResponsiveContainer width={"99%"} minHeight={300} className={classes.root}>
          <ComposedChart data={rows} margin={{ bottom: 20, top: 10, left: 15 }}>
            <CartesianGrid chartHeight={40} strokeDasharray="3 3" vertical={false} />
            <XAxis height={70} dataKey="dealer" type="category" interval={0} axisLine={false} tickLine={false} tick={CustomizedXAxisTick} />
            <YAxis
              width={40}
              axisLine={false}
              tickLine={false}
              tick={({ x, y, payload }) => {
                return formatYTick(x, y, payload, series);
              }}
            />
            <Tooltip
              content={<BarTooltip dealers={dealers} query={query} extraBenchmarks={extraBenchmarks} />}
              position={isDesktopOrLaptop ? { x: 500, y: 0 } : { x: 0, y: 0 }}
            />
            {chartType == "bar" && <StackedChartBar dataKey="actual" barSize={20} fill={d3Scale.schemeDark2[1]} />}
            {chartType == "period" && <StackedChartBar dataKey="actual-1" barSize={20} fill={d3Scale.schemeDark2[2]} name={rows[0].dates[1]} />}
            {chartType == "period" && <StackedChartBar dataKey="actual" barSize={20} fill={d3Scale.schemeDark2[1]} name={rows[0].dates[0]} />}
            {showBenchmark && (
              <Line
                name={benchmarks[0]}
                stroke={benchmarkLine}
                fill={benchmarkLine}
                strokeWidth={3}
                dataKey="benchmark"
                dot={false}
                strokeDasharray="3 3"
                strokeLinejoin="round"
              />
            )}
            {showMedian && (
              <Line
                name={benchmarks[1]}
                stroke={medianLine}
                fill={medianLine}
                strokeWidth={3}
                dataKey="median"
                dot={false}
                strokeDasharray="3 3"
                strokeLinejoin="round"
              />
            )}
            {extraBenchmarks
              ? extraBenchmarks.map((b, i) => {
                  const benchmarkColor = getBenchmarkColor(b, available, dealers.length);
                  return (
                    <Line
                      name={b}
                      stroke={benchmarkColor}
                      fill={benchmarkColor}
                      strokeDasharray="5, 5"
                      strokeWidth={2}
                      dataKey={`extra_${b}`}
                      dot={false}
                      strokeLinejoin="round"
                    />
                  );
                })
              : null}
            <Legend content={renderLegend} verticalAlign="top" height={36} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Paper>
  );
};

type ReportMenuProps = {
  query: Query;
  handleShowToggle: (attr: string) => (e: React.SyntheticEvent<any>) => void;
  state: {
    showBenchmark: boolean;
    showMedian: boolean;
    showTarget: boolean;
    forcedHeight: string;
  };
  hide?: boolean;
  selectedDealers?: any[];
  setSelectedDealers?: any;
  classes: any;
  extraBenchmarks?: string[];
  setExtraBenchmarks?: any;
};

const ReportMenu: React.FunctionComponent<ReportMenuProps> = ({
  query,
  handleShowToggle,
  state,
  hide,
  selectedDealers,
  setSelectedDealers,
  classes,
  setExtraBenchmarks,
  extraBenchmarks,
}) => {
  const toggleBenchmark = handleShowToggle("showBenchmark");
  const toggleMedian = handleShowToggle("showMedian");
  const toggleTarget = handleShowToggle("showTarget");
  const { oems } = useContext(OemContext);
  const { selected: benchmarks } = useContext(BenchmarkContext);
  const dealer = useContext(DealerContext);
  const [showSelection, setShowSelection] = React.useState<boolean>(false);
  const setBenches = benchies => {
    const add = benchies.filter(t => !benchmarks.includes(t) && t !== "");
    setExtraBenchmarks([...add]);
  };

  const handleSelectedDealers = (value: string) => {
    const filtered = (selectedDealers || []).filter(dealer => dealer.name != value);
    const dealer = (selectedDealers || []).find(d => d.name == value);
    setSelectedDealers([...filtered, { ...dealer, active: !dealer.active }]);
  };

  const compare = (a, b) => {
    if (a.pos > b.pos) {
      return 1;
    }
    if (a.pos < b.pos) {
      return -1;
    }
    return 0;
  };

  const sortedDealers = React.useMemo(() => {
    return selectedDealers.sort(compare);
  }, [selectedDealers]);

  return (
    <div hidden={hide}>
      <ChartMenu showLeft={true}>
        <Grid container justify="flex-end" style={{ padding: "0.5em 1em" }}>
          <Grid item>
            <Button
              size="small"
              onClick={() => {
                setSelectedDealers(
                  selectedDealers.map(d => {
                    return { ...d, active: true };
                  }),
                );
              }}
            >
              Select All
            </Button>
            &nbsp;
            <Button
              size="small"
              onClick={() => {
                setSelectedDealers(
                  selectedDealers.map(d => {
                    return { ...d, active: false };
                  }),
                );
              }}
            >
              Deselect All
            </Button>
          </Grid>
        </Grid>
        {benchmarks.filter(bm => bm.length).length > 0 && (
          <>
            <MenuItem onClick={toggleBenchmark}>
              <Checkbox onChange={toggleBenchmark} checked={state.showBenchmark} disableRipple />
              {benchmarks[0]}
            </MenuItem>
            {benchmarks.length > 1 && (
              <MenuItem onClick={toggleMedian}>
                <Checkbox onChange={toggleMedian} checked={state.showMedian} disableRipple />
                {benchmarks[1]}
              </MenuItem>
            )}
          </>
        )}
        <div style={{ maxHeight: "150px", overflowY: "scroll" }}>
          {(sortedDealers || []).map(dealer => (
            <MenuItem onClick={() => handleSelectedDealers(dealer.name)} disabled={dealer.active && sortedDealers.filter(d => d.active).length <= 1}>
              <Checkbox checked={dealer.active} disableRipple />
              {dealer.name}
            </MenuItem>
          ))}
        </div>
        <hr />
        <MenuItem>
          <Button
            onClick={() => {
              setShowSelection(true);
            }}
          >
            Add Other Benchmarks
          </Button>
        </MenuItem>
        <div>
          <ClickAwayListener
            onClickAway={() => {
              setShowSelection(false);
            }}
          >
            {showSelection && (
              <BenchmarkSelection
                allowEmptyValue={false}
                dealers={dealer.selectedDealers}
                setBenchmark={setBenches}
                benchmark={[...benchmarks, ...extraBenchmarks]}
                hidePeriods={false}
                allowMultiple={true}
                className={classnames(classes.searchInput, classes.benchmarkSelection, classes.mediumInput)}
                allowMany={true}
              />
            )}
          </ClickAwayListener>
        </div>
      </ChartMenu>
    </div>
  );
};
const ChartUnstyled: React.FunctionComponent<ChartProps> = ({
  classes,
  theme,
  report,
  title,
  query,
  showBenchmark,
  handleSelection,
  showMedian,
  showTarget,
  measureAgainst,
  renderMenu,
  forcedHeight,
  chartOptions,
  chartType,
  measure,
  type = undefined,
  openDialog,
  setOpenDialog,
  isDesktopOrLaptop,
  lockScorecardOptions = false,
}) => {
  const [extraBenchmarks, setExtraBenchmarks] = React.useState<string[]>([]);
  const { getGroupBarSeries } = React.useContext(GroupBarChartContext);
  const series = getGroupBarSeries(report, { ...query, benchmarks: [...query.benchmarks, ...extraBenchmarks] }, measure);
  const { selectedDealers: dealerList } = useContext(DealerContext);
  const [selectedDealers, setSelectedDealers] = React.useState<any[]>(dealerList.map((dealer, indx) => ({ name: dealer.dealer, active: true, pos: indx })));

  useEffect(() => setSelectedDealers(dealerList.map((dealer, indx) => ({ name: dealer.dealer, active: true, pos: indx }))), [dealerList]);

  const [state, setState] = React.useState({
    showMedian: showMedian || false,
    showTarget: showTarget || false,
    showBenchmark: showBenchmark || false,
    forcedHeight: forcedHeight,
  });

  const handleShowToggle = attr => event => {
    setState({
      ...state,
      [attr]: !state[attr],
    });
  };

  if (!series.loading.loaded) {
    return <Loading />;
  }

  return (
    <ChartInner
      extraBenchmarks={extraBenchmarks}
      measureAgainst={measureAgainst}
      showBenchmark={state.showBenchmark}
      showMedian={state.showMedian}
      showTarget={state.showTarget}
      handleSelection={handleSelection}
      report={report}
      theme={theme}
      title={title || series.series.name}
      selectedDealers={selectedDealers}
      series={series.series}
      classes={classes}
      menu={
        <>
          {isDesktopOrLaptop && !openDialog && (
            <Button onClick={() => setOpenDialog(openDialog ? false : true)}>
              <ZoomIn />
            </Button>
          )}
          {renderMenu && renderMenu()}
          <ReportMenu
            state={state}
            handleShowToggle={handleShowToggle}
            query={query}
            selectedDealers={selectedDealers}
            setSelectedDealers={setSelectedDealers}
            setExtraBenchmarks={setExtraBenchmarks}
            extraBenchmarks={extraBenchmarks}
            classes={classes}
          />
        </>
      }
      forcedHeight={state.forcedHeight}
      query={query}
      chartOptions={chartOptions}
      chartType={chartType}
    />
  );
};

export const GroupBarChart = withTheme()(withStyles(styles)(ChartUnstyled));
export const GroupBarChartAlt = withTheme()(withStyles(stylesAlt)(ChartUnstyled));

export type SelectableChartProps = {
  query: Query;
  title?: string;
  report: Report;
  handleSelection?(key: Report): void;
  showBenchmark?: boolean;
  showMedian?: boolean;
  showTarget?: boolean;
  chartOptions?: any;
  period?: string;
  type?: string;
  handleChartType?: (chartType: "bar" | "line" | "pie" | "period") => void;
  chartType?: "bar" | "line" | "pie" | "period";
  allowPieSelection?: boolean;
  measure?: string;
  lockScorecardOptions?: boolean;
};

export const SelectableChart: React.FC<SelectableChartProps> = ({
  handleSelection,
  report,
  title,
  query,
  showMedian,
  showTarget,
  showBenchmark,
  chartOptions,
  period = "Monthly",
  type = undefined,
  chartType,
  handleChartType,
  allowPieSelection = false,
  lockScorecardOptions = false,
  measure,
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [selectedChart, setSelectedChart] = React.useState(lockScorecardOptions ? "ytd" : period);
  const [menuAnchor, setMenuAnchor] = React.useState<any>(null);
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const [chartTypeMenuOpen, setChartTypeMenuOpen] = React.useState(false);
  const [chartTypeMenuAnchor, setChartTypeMenuAnchor] = React.useState(null);

  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-width: 1280px)",
  });

  const variantMap = {
    Monthly: "monthly",
    Quarterly: "quarter",
    Yearly: "yearly",
    "Full Year": "fullyear",
    YTD: "ytd",
    "3RM": "threerm",
  };

  return (
    <>
      <GroupBarChart
        handleSelection={handleSelection}
        report={report}
        title={title}
        query={{ ...query, meta: variantMap[selectedChart] ? { ...query.meta, variant: variantMap[selectedChart] } : { ...query.meta } }}
        showMedian={showMedian}
        showTarget={showTarget}
        showBenchmark={showBenchmark}
        chartOptions={chartOptions}
        chartType={chartType}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        isDesktopOrLaptop={isDesktopOrLaptop}
        measure={measure}
        renderMenu={() => (
          <>
            {chartType && handleChartType && (
              <Button
                onClick={e => {
                  setChartTypeMenuOpen(true);
                  setChartTypeMenuAnchor(e.currentTarget);
                }}
              >
                {chartType == "bar" && <span>Bar</span>}
                {chartType == "line" && <span>Line</span>}
                {chartType == "pie" && <span>Pie</span>}
                {chartType == "period" && <span>Period</span>}
              </Button>
            )}
            <Button
              disabled={lockScorecardOptions}
              onClick={e => {
                setMenuOpen(true);
                setMenuAnchor(e.currentTarget);
              }}
            >
              <History /> ({selectedChart})
            </Button>
          </>
        )}
      />
      {
        <>
          <Menu anchorEl={menuAnchor} open={menuOpen} onClose={() => setMenuOpen(false)}>
            <MenuItem
              selected={selectedChart == "YTD"}
              onClick={() => {
                setMenuOpen(false);
                setSelectedChart("YTD");
              }}
            >
              YTD
            </MenuItem>
            <MenuItem
              selected={selectedChart == "Monthly"}
              onClick={() => {
                setMenuOpen(false);
                setSelectedChart("Monthly");
              }}
            >
              Monthly
            </MenuItem>
            <MenuItem
              selected={selectedChart == "3RM"}
              onClick={() => {
                setMenuOpen(false);
                setSelectedChart("3RM");
              }}
            >
              3RM
            </MenuItem>
            <MenuItem
              selected={selectedChart == "Quarterly"}
              onClick={() => {
                setMenuOpen(false);
                setSelectedChart("Quarterly");
              }}
            >
              Quarterly
            </MenuItem>
            <MenuItem
              selected={selectedChart == "Yearly"}
              onClick={() => {
                setMenuOpen(false);
                setSelectedChart("Yearly");
              }}
            >
              Yearly
            </MenuItem>
            <MenuItem
              selected={selectedChart == "Full Year"}
              onClick={() => {
                setMenuOpen(false);
                setSelectedChart("Full Year");
              }}
            >
              Full Year
            </MenuItem>
          </Menu>
          {chartType && handleChartType && (
            <Menu anchorEl={chartTypeMenuAnchor} open={chartTypeMenuOpen} onClose={() => setChartTypeMenuOpen(false)}>
              <MenuItem
                selected={chartType === "bar"}
                onClick={() => {
                  setChartTypeMenuOpen(false);
                  handleChartType("bar");
                }}
              >
                Bar Chart
              </MenuItem>
              <MenuItem
                selected={chartType === "line"}
                onClick={() => {
                  setChartTypeMenuOpen(false);
                  handleChartType("line");
                }}
              >
                Line Chart
              </MenuItem>
              <MenuItem
                selected={chartType === "pie"}
                disabled={!allowPieSelection}
                onClick={() => {
                  setChartTypeMenuOpen(false);
                  handleChartType("pie");
                }}
              >
                Pie Chart
              </MenuItem>
              <MenuItem
                selected={chartType === "period"}
                onClick={() => {
                  setChartTypeMenuOpen(false);
                  handleChartType("period");
                }}
              >
                Period over Period
              </MenuItem>
            </Menu>
          )}
        </>
      }
      {isDesktopOrLaptop && (
        <ChartDialog
          handleSelection={handleSelection}
          report={report}
          title={title}
          query={{ ...query, meta: variantMap[selectedChart] ? { ...query.meta, variant: variantMap[selectedChart] } : { ...query.meta } }}
          showMedian={showMedian}
          showTarget={showTarget}
          showBenchmark={showBenchmark}
          chartOptions={chartOptions}
          setMenuOpen={setMenuOpen}
          setMenuAnchor={setMenuAnchor}
          selectedChart={selectedChart}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          isDesktopOrLaptop={isDesktopOrLaptop}
          chart={props => {
            return (
              <GroupBarChart
                handleSelection={handleSelection}
                report={report}
                title={title}
                query={{ ...query, meta: variantMap[selectedChart] ? { ...query.meta, variant: variantMap[selectedChart] } : { ...query.meta } }}
                showMedian={showMedian}
                showTarget={showTarget}
                showBenchmark={showBenchmark}
                chartOptions={chartOptions}
                chartType={chartType}
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                isDesktopOrLaptop={isDesktopOrLaptop}
                measure={measure}
                renderMenu={() => (
                  <Button
                    disabled={lockScorecardOptions}
                    onClick={e => {
                      setMenuOpen(true);
                      setMenuAnchor(e.currentTarget);
                    }}
                  >
                    <History /> ({selectedChart})
                  </Button>
                )}
              />
            );
          }}
        />
      )}
    </>
  );
};
